import { inject, Injectable } from "@angular/core";
import { GlossSyncService } from "../../gloss.sync.service";
import { BasiqMapType } from "../../../../models/types/basiq.types";
import { SyncStatusEnum } from "../../../../models/types/general-types";
import { AccountView } from "../../../../models/view/account/account.view";
import { BasiqTransactionMapper } from "./basiq.mapper.transaction";
import { BasiqIoTransactionType } from "../../../../models/types/basiq-io-transaction.types";
import { AppStateStoreService } from "../../../store/app-state/app-state.store.service";
import { TransactionCategoryMap } from "@bitwarden/web-vault/app/services/syncing/basiq/mappers/basiq.mapper.category";
import { BasiqCategoryAction } from "@bitwarden/web-vault/app/services/syncing/basiq/actions/basiq.action.category";

@Injectable({
  providedIn: "root",
})
export class BasiqMapper {
  private glossSyncService: GlossSyncService;
  private appState: AppStateStoreService;
  private basiqTransactionMapper: BasiqTransactionMapper;

  totalAmount = 0;
  mappedBasiqData: BasiqMapType;
  syncStatus: SyncStatusEnum;

  constructor() {
    this.basiqTransactionMapper = inject(BasiqTransactionMapper);
    this.appState = inject(AppStateStoreService);
    this.glossSyncService = inject(GlossSyncService);
  }

  async mapBasiqData(
    accountView: AccountView,
    rawBasiqIoTransactions: BasiqIoTransactionType[],
    transactionCategoryMap: TransactionCategoryMap,
    basiqCategoryAction: BasiqCategoryAction,
  ) {
    try {
      this.totalAmount = 0;
      const mappedBasiqData: BasiqMapType = {
        categories: [],
        transactions: [],
        sourceTransactions: [],
        pendingTransactions: [],
      };
      rawBasiqIoTransactions.forEach((rawBasiqIoTransaction) => {
        const categoryNames =
          basiqCategoryAction.categoryNamesFromBasiqTransaction(rawBasiqIoTransaction);
        const transaction = this.basiqTransactionMapper.toTransactionView(
          accountView,
          rawBasiqIoTransaction,
          transactionCategoryMap.get(rawBasiqIoTransaction.id),
        );
        const sourceTransaction = this.basiqTransactionMapper.toSourceTransactionView(
          accountView,
          transaction,
          categoryNames,
        );
        transaction.sourceLink.id = sourceTransaction.id;

        mappedBasiqData.transactions.push(transaction);
        mappedBasiqData.sourceTransactions.push(sourceTransaction);

        this.totalAmount += Number(rawBasiqIoTransaction.amount);
      });

      this.mappedBasiqData = mappedBasiqData;
      return true;
    } catch (error) {
      await this.glossSyncService.logError(
        [accountView],
        error,
        "mapping transactions",
        SyncStatusEnum.cantMapTransactions,
      );
      return false;
    }
  }

  async mapTransactionData(
    transactions: BasiqIoTransactionType[],
    account: AccountView,
    transactionCategoryMap: TransactionCategoryMap,
    basiqCategoryAction: BasiqCategoryAction,
  ) {
    try {
      const processMessage = `Mapping Transactions of ${account.name}`;
      this.appState.startProcess("importing-data", processMessage);
      if (!transactions.length) {
        const error = new Error(`No transactions to map. Account: ${account.name}`);
        await this.glossSyncService.logError(
          [account],
          error,
          "mapping transactions",
          SyncStatusEnum.cantMapTransactions,
        );
        this.syncStatus = SyncStatusEnum.cantMapTransactions;
        this.appState.endProcess("importing-data");
        return false;
      }
      const isMapped = await this.mapBasiqData(
        account,
        transactions,
        transactionCategoryMap,
        basiqCategoryAction,
      );
      this.appState.endProcess("importing-data");
      return isMapped;
    } catch (error) {
      await this.glossSyncService.logError(
        [account],
        error,
        "mapping transactions",
        SyncStatusEnum.unknownError,
      );
      this.syncStatus = SyncStatusEnum.cantMapTransactions;
      this.appState.endProcess("importing-data");
      this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
      return false;
    }
  }
}
